import type { QuickRebuttal, SalesChecklist } from "@expert/contentful";

import { useNotification } from "@expert/common-ui";
import { Box, Button, Stack, Text } from "@mantine/core";
import { useCallback, useState } from "react";

import type { EligibleProduct } from "../../../../types";

import { createEnrollment, EnrollmentStatus } from "../../../../api/enrollment";
import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../../stores";
import { SaleProgress } from "../../../../types";
import { FullOfferChecklist } from "../FullOfferChecklist/FullOfferChecklist";
import { LightBulbIcon } from "../Icons";
import { SalesFooter } from "./SalesFooter";
import styles from "./SalesFooter.module.css";

function formatQuickRebuttal(doc?: QuickRebuttal): string {
    if (!doc?.objections?.length) return "";
    const q = (s = "") => `“${s}”`;
    return doc.objections
        .map((objection, i) => {
            const { title, responses = [] } = objection;
            const outerListItem = `${i + 1}. ${title === "Early Rebuttal" ? title : q(title)}`;
            const innerListItems = responses.map((response) => `- ${q(response.text)}`).join("\n");
            return innerListItems ? `${outerListItem}\n${innerListItems}` : outerListItem;
        })
        .join("\n\n");
}

interface FullOfferChecklistStepProps {
    product: EligibleProduct;
    salesChecklist?: SalesChecklist;
    quickRebuttal?: QuickRebuttal;
}

export function FullOfferChecklistStep({ product, salesChecklist, quickRebuttal }: FullOfferChecklistStepProps) {
    const {
        logger,
        onQuickRebuttal,
        features,
        sessionId,
        partner,
        apiClients,
        analytics,
        taskData,
        taskType,
        onPlayDisclosure,
    } = useSalesContext();
    const notification = useNotification();

    const useEnrollVariant = product.productSku === "HOMEPRO" && features.isAudioDisclosuresEnabled;

    const [hasCompletedDisclosure, setHasCompletedDisclosure] = useState(false);
    // Stable identity so RecordedLegalDisclosuresSection's effect (which lists this in deps)
    // doesn't re-fire and re-`restart` the timer on every parent render.
    const handleDisclosureCompleted = useCallback(() => setHasCompletedDisclosure(true), []);

    // Mirror RecordedLegalDisclosuresSection's render gate: when the section won't render
    // (e.g., non-English locale, chat task), there's no disclosure to gate Enroll on.
    const isEnglishOrUnset = taskData.locale === "en-us" || taskData.locale === undefined;
    const requiresDisclosure = useEnrollVariant && taskType.isVoice && isEnglishOrUnset && !!onPlayDisclosure;

    const onEnroll = async () => {
        void analytics.dispatcher.dispatchAction("ButtonClick", "Enroll", {
            productSku: product.productSku,
        });

        const enroll = {
            callId: taskData.asurionCallId ?? sessionId,
            sessionId,
            partner,
            productOfferId: product.id,
            productSku: product.productSku,
        };

        const response = await createEnrollment(apiClients.sales, logger, analytics, enroll);

        if (response.enrollmentStatus === EnrollmentStatus.Success) {
            useProductOfferStore.getState().updateProduct(sessionId, product.id, {
                ...product,
                saleProgress: SaleProgress.Success,
            });
        } else {
            logger.warn(
                {
                    productSku: product.productSku,
                    productOfferId: product.id,
                    sessionId,
                    partner,
                    enrollmentStatus: response.enrollmentStatus,
                },
                "Enrollment did not succeed",
            );
            notification.error("There was an error processing the enrollment");
        }
    };

    const QuickRebuttalButton = () => (
        <Button
            className={styles.quickRebuttalButton}
            variant="outline"
            radius="lg"
            fz="xs"
            size="sm"
            leftSection={<LightBulbIcon />}
            onClick={() => onQuickRebuttal?.(formatQuickRebuttal(quickRebuttal))}
            color="white"
        >
            Quick rebuttal
        </Button>
    );

    if (!salesChecklist) {
        logger.error({ product, salesChecklist }, "Full offer checklist failed to render");
    }

    return (
        <Stack pos="relative" flex={1}>
            <Box flex={1} style={{ overflowY: "auto" }}>
                {product.saleProgress === SaleProgress.InProgress && salesChecklist ? (
                    <FullOfferChecklist
                        salesChecklist={salesChecklist}
                        onDisclosureCompleted={handleDisclosureCompleted}
                    />
                ) : (
                    <Text mt="20%" c="dark.1" size="sm">
                        Offer checklist is currently unavailable. Try again later
                    </Text>
                )}
            </Box>
            <SalesFooter
                product={product}
                buttonSlot={quickRebuttal && <QuickRebuttalButton />}
                continueButtonProps={
                    useEnrollVariant
                        ? {
                              text: "Enroll",
                              onClick: onEnroll,
                              isDisabled: requiresDisclosure && !hasCompletedDisclosure,
                          }
                        : undefined
                }
            />
        </Stack>
    );
}
